import isNil from 'lodash/isNil';
import {ReactNode, useCallback, useEffect, useState} from 'react';
import {TrackPageViewContainer} from '../../../../common/containers/TrackPageViewContainer';
import {Loading} from '../../../../design-system/atoms/Loading/Loading';
import {Maybe} from '../../../../graphql/generated/types';
import {useInfiniteBestCommentListQuery} from '../../../../wrapper/storeWrapper';
import {COMMENT_TAB_TEXT_MAP, CommentTabType} from '../../../comment/constant';
import {CommentTabContainerProps} from './CommentTabContainer';

interface CommentTabControlAndLogContainerProps {
  seriesId: number;
  productId: number;
  initialSortType?: Maybe<string>;
  children: (props: Omit<CommentTabContainerProps, 'children'>) => ReactNode;
}

export function CommentTabControlAndLogContainer({
  seriesId,
  productId,
  initialSortType,
  children,
}: CommentTabControlAndLogContainerProps) {
  // 베스트 댓글 활성화 체크 완료 여부
  const [checkCompleted, setCheckCompleted] = useState(!!initialSortType);
  const [currentTab, setCurrentTab] = useState(CommentTabType.All);

  // 초기 로드 시 BEST | ALL 탭 선택 여부 확인하는데 API 에서 따로 플래그를 내려주지 않고 있어 API 통해 직접 확인합니다.
  const {data: bestCommentList} = useInfiniteBestCommentListQuery(
    {
      bestCommentListInput: {
        page: 0,
        seriesId,
        productId,
      },
    },
    {
      enabled: !initialSortType, // sortOpt 를 가지고 오면 무조건 ALL 탭을 보여주기 때문에 굳이 BEST 댓글 목록을 가져올 필요가 없습니다.
      initialPageParam: {
        bestCommentListInput: {
          page: 0,
          seriesId,
          productId,
        },
      },
      getNextPageParam: () => undefined,
    }
  );

  useEffect(() => {
    if (isNil(bestCommentList)) {
      return;
    }
    if (checkCompleted) {
      return;
    }
    /**
     *  Best 탭에 댓글이 5개 이상일 경우, Best탭이 디폴트. 그 외는 전체탭이 디폴트입니다.
     */
    setCurrentTab(
      (bestCommentList.pages?.[0]?.bestCommentList?.commentList?.length ?? 0) <
        5
        ? CommentTabType.All
        : CommentTabType.Best
    );
    setCheckCompleted(true);
  }, [bestCommentList, checkCompleted]);

  const onChangeTab = useCallback(
    (tab: CommentTabType) => setCurrentTab(tab),
    []
  );

  if (!checkCompleted) {
    return (
      <div className="flex-center-full-auto">
        <Loading />
      </div>
    );
  }

  return (
    <>
      {children({
        seriesId,
        productId,
        initialSortType,
        currentTab,
        onChangeTab,
      })}
      <TrackPageViewContainer
        key={currentTab}
        trackObj={{
          section: '댓글',
          page: `댓글_메인_${COMMENT_TAB_TEXT_MAP[currentTab].text}`,
          actionType: 'Pageview',
          actionName: '댓글_메인_화면',
          customProps: {
            user_action: 'pv',
          },
          page_meta: {
            id: String(productId || seriesId),
            type: productId ? 'productId' : 'seriesId',
            series_id: String(seriesId),
          },
        }}
      />
    </>
  );
}
